import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { telHref } from '../utils/quinn';
import { useIsEditing } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

const mapCtaDef: SlotGroupDefinition = {
  label: 'Map Button',
  description: 'Shown when the property has a SightMap.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Availability on the Map' },
    url: { label: 'URL', type: 'url', defaultValue: '/interactive-map' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button (no map)',
  description: 'Without a SightMap the band offers a tour and a call instead. No self-guided tour URL → Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const titleClass = 'm-0 font-heading text-[30px] font-light leading-9 max-[767px]:text-[28px] max-[767px]:leading-[34px] [&_p]:m-0';
const leadClass = 'm-0 text-[15px] leading-[22px] opacity-[.85] [&_p]:m-0';

/** Primary band to the interactive site map; with no SightMap it is the standard tour + call band. */
export function InteractiveMapCallout() {
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const map = useSlotGroup('interactivemapcallout.map_cta', mapCtaDef);
  const tour = useSlotGroup('interactivemapcallout.tour_cta', tourCtaDef);
  const hasMap = flags.shows('interactive-map');
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const tel = telHref(flags.phone);

  return (
    <section className="flex items-center justify-between gap-12 bg-[--q-primary] px-[--gutter] py-10 text-[--q-on-primary] [&_.btn-on-image-ghost]:border-[--q-on-primary] [&_.btn-on-image-ghost]:text-[--q-on-primary] [&_a:focus-visible]:outline-[--q-accent-on-primary] max-[767px]:flex-col max-[767px]:items-stretch max-[767px]:gap-4 max-[767px]:py-8">
      {hasMap || editing ? (
        <div className="flex flex-col gap-1.5">
          <RichTextContent path="interactivemapcallout.title" as="h2" className={titleClass} />
          <RichTextContent path="interactivemapcallout.lead" as="p" className={leadClass} />
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <RichTextContent path="interactivemapcallout.fallback_title" as="h2" className={titleClass} />
          <RichTextContent path="interactivemapcallout.fallback_lead" as="p" className={leadClass} />
        </div>
      )}
      <div className="flex shrink-0 gap-3 max-[767px]:flex-col max-[767px]:gap-2.5">
        <EditableSlotGroup groupPath="interactivemapcallout.map_cta" definition={mapCtaDef}>
          {hasMap && (
            <a className="btn btn-on-image" href={map.url || PATHS.interactiveMap} target={map._target || '_self'}>
              {map.label || 'View Availability on the Map'} <span className="btn__arrow">→</span>
            </a>
          )}
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="interactivemapcallout.tour_cta" definition={tourCtaDef}>
          {!hasMap && (
            <a
              className="btn btn-on-image"
              href={tourLink.href}
              target={tourLink.isTour ? tour._target || '_self' : '_self'}
              data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
            >
              {tourLink.label}
            </a>
          )}
        </EditableSlotGroup>
        {!hasMap && tel && (
          <a className="btn btn-on-image-ghost dniphonehref" href={tel}>
            Call <span className="dniphone">{flags.phone}</span>
          </a>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapCallout',
  displayName: 'Interactive Map Callout',
  tokens: {
    'interactivemapcallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>See which homes are open, street by street.</p>',
      generator: {
        prompt: 'A short line inviting people to find an open home on the interactive site map, in the words that fit how this community is laid out (streets of homes, buildings, floors).',
        context: 'Title of the green band between the floor plan list and the details on the Floor Plans page, beside a "View Availability on the Map" button.',
        length: 'under 10 words',
      },
    },
    'interactivemapcallout.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue: '<p>The interactive site map shows every home and which ones you can lease now.</p>',
    },
    'interactivemapcallout.fallback_title': {
      label: 'Title (no map)',
      type: 'richtext',
      defaultValue: '<p>Picture yourself here?</p>',
    },
    'interactivemapcallout.fallback_lead': {
      label: 'Lead (no map)',
      type: 'richtext',
      defaultValue: '<p>Book a self-guided tour and see the community on your own schedule.</p>',
    },
  },
  slotGroups: {
    'interactivemapcallout.map_cta': mapCtaDef,
    'interactivemapcallout.tour_cta': tourCtaDef,
  },
};

(InteractiveMapCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteractiveMapCallout;
